<script lang="ts">
export default {
  name: 'TextField',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue?: string | number;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    error?: boolean;
    errorMessage?: string;
    hint?: string;
    type?: string;
    class?: string;
    prependIcon?: any;
    appendIcon?: any;
  }>(),
  {
    disabled: false,
    readonly: false,
    error: false,
    type: 'text',
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
}>();

const inputClasses = computed(() => {
  const classes = ['input', 'input-bordered', 'w-full'];
  
  if (props.error) {
    classes.push('input-error');
  }
  
  if (props.disabled) {
    classes.push('input-disabled');
  }
  
  if (props.class) {
    classes.push(props.class);
  }
  
  return classes.join(' ');
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', props.type === 'number' ? Number(target.value) || 0 : target.value);
};
</script>

<template>
  <div class="form-control w-full">
    <label v-if="label" class="label">
      <span class="label-text text-sm">{{ label }}</span>
    </label>
    
    <div class="relative w-full">
      <div v-if="prependIcon" class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <component :is="prependIcon" class="w-5 h-5 text-gray-400" />
      </div>
      
      <input
        :type="type"
        :class="[inputClasses, { 'pl-10': prependIcon, 'pr-10': appendIcon }]"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        @input="handleInput"
      />
      
      <div v-if="appendIcon" class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <component :is="appendIcon" class="w-5 h-5 text-gray-400" />
      </div>
    </div>
    
    <!-- whitespace-normal undoes DaisyUI's .label nowrap, which makes a message longer than
         the field overflow into whatever sits beside it instead of wrapping. -->
    <label v-if="error && errorMessage" class="label whitespace-normal">
      <span class="label-text-alt text-error">{{ errorMessage }}</span>
    </label>
    
    <label v-else-if="hint" class="label">
      <span class="label-text-alt text-gray-500">{{ hint }}</span>
    </label>
  </div>
</template>
